import { useState } from "react";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ScrollReveal } from "@/components/ScrollReveal";
import { Send, CheckCircle2, Loader2, Clock, Mail, MapPin, Sparkles } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const motifs = [
  "Découvrir Grafton IA",
  "Démonstration personnalisée",
  "Offre Starter",
  "Offre Medium / Premium",
  "Partenariat Agence Immobilière",
  "Atelier",
  "Séminaire Martinique",
  "Investir dans Grafton IA",
  "Autre",
];

const secteurs = [
  "Immobilier", "Commerce / Retail", "Tech / SaaS", "Conseil",
  "Santé", "Finance", "BTP", "Restauration", "Services", "Autre",
];

const budgets = [
  "< 1 000€/mois", "1 000€ - 2 000€/mois", "2 000€ - 3 000€/mois", "> 3 000€/mois", "À définir",
];

const delais = ["Immédiat", "1 mois", "3 mois", "6 mois", "En réflexion"];

const ContactPage = () => {
  const [form, setForm] = useState({
    prenom: "", nom: "", email: "", telephone: "",
    entreprise: "", secteur: "", motif: "", budget: "", delai: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const updateField = (field: string, value: string) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.prenom || !form.nom || !form.email || !form.motif || !form.message) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSent(true);
        toast.success("Message envoyé avec succès !");
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } catch {
      toast.error("Erreur réseau");
    }
    setSending(false);
  };

  if (sent) {
    return (
      <PublicLayout>
        <section className="py-20 md:py-32">
          <div className="container max-w-lg text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h1 className="text-3xl font-bold mb-4">Message envoyé !</h1>
            <p className="text-muted-foreground mb-8">
              Merci pour votre intérêt. Joël vous répondra personnellement dans les <strong>4 heures maximum</strong>.
            </p>
            <Button variant="hero" onClick={() => window.location.href = "/"} data-testid="back-home-btn">
              Retour à l'accueil
            </Button>
          </div>
        </section>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="py-16 md:py-24">
        <div className="container max-w-4xl">
          <div className="grid md:grid-cols-5 gap-10">
            {/* Left info */}
            <div className="md:col-span-2 space-y-6">
              <ScrollReveal>
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-semibold mb-4">
                    <Sparkles className="h-3.5 w-3.5" /> Contact direct
                  </div>
                  <h1 data-testid="contact-title" className="text-3xl md:text-4xl font-bold leading-tight mb-4">
                    Parlons de votre <span className="text-gradient">projet</span>
                  </h1>
                  <p className="text-muted-foreground leading-relaxed">
                    Joël Broulou, fondateur de Grafton IA, vous répond personnellement
                    en moins de 4 heures.
                  </p>
                </div>
              </ScrollReveal>

              <ScrollReveal delay={100}>
                <div className="space-y-4 pt-4">
                  {[
                    { icon: Clock, text: "Réponse garantie sous 4h" },
                    { icon: Mail, text: "graftonia1@gmail.com" },
                    { icon: MapPin, text: "Paris, France" },
                  ].map((item) => (
                    <div key={item.text} className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                        <item.icon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm text-muted-foreground">{item.text}</span>
                    </div>
                  ))}
                </div>
              </ScrollReveal>
            </div>

            {/* Form */}
            <div className="md:col-span-3">
              <ScrollReveal delay={150}>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <Card>
                    <CardContent className="p-6 space-y-4">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Prénom *</label>
                          <Input data-testid="contact-prenom" value={form.prenom} onChange={(e) => updateField("prenom", e.target.value)} placeholder="Votre prénom" required />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Nom *</label>
                          <Input data-testid="contact-nom" value={form.nom} onChange={(e) => updateField("nom", e.target.value)} placeholder="Votre nom" required />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Email *</label>
                          <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)} placeholder="vous@entreprise.com" required />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Téléphone</label>
                          <Input value={form.telephone} onChange={(e) => updateField("telephone", e.target.value)} placeholder="06 12 34 56 78" />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Entreprise</label>
                          <Input value={form.entreprise} onChange={(e) => updateField("entreprise", e.target.value)} placeholder="Ma Société SAS" />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Secteur</label>
                          <Select value={form.secteur} onValueChange={(v) => updateField("secteur", v)}>
                            <SelectTrigger className="h-10"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                            <SelectContent>{secteurs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Motif du contact *</label>
                        <Select value={form.motif} onValueChange={(v) => updateField("motif", v)}>
                          <SelectTrigger data-testid="contact-motif" className="h-10"><SelectValue placeholder="Choisir un motif" /></SelectTrigger>
                          <SelectContent>{motifs.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Budget</label>
                          <Select value={form.budget} onValueChange={(v) => updateField("budget", v)}>
                            <SelectTrigger className="h-10"><SelectValue placeholder="Budget" /></SelectTrigger>
                            <SelectContent>{budgets.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-muted-foreground mb-1 block">Délai</label>
                          <Select value={form.delai} onValueChange={(v) => updateField("delai", v)}>
                            <SelectTrigger className="h-10"><SelectValue placeholder="Délai" /></SelectTrigger>
                            <SelectContent>{delais.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Votre message *</label>
                        <Textarea data-testid="contact-message" value={form.message} onChange={(e) => updateField("message", e.target.value)} placeholder="Décrivez votre projet, vos objectifs et comment Grafton IA peut vous aider..." rows={4} required />
                      </div>

                      <Button type="submit" variant="hero" size="lg" className="w-full h-12 gap-2" disabled={sending} data-testid="contact-submit-btn">
                        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        {sending ? "Envoi en cours..." : "Envoyer mon message"}
                      </Button>

                      <p className="text-[10px] text-muted-foreground text-center">
                        Vos données sont traitées de manière confidentielle. Réponse sous 4h maximum.
                      </p>
                    </CardContent>
                  </Card>
                </form>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
};

// Need Card import
import { Card, CardContent } from "@/components/ui/card";

export default ContactPage;
